/**
 * Ultra-Optimized Free Shipping Progress Bar
 * Minimalist Mobile-First Implementation with Conversion-Optimized Text
 */
(function() {
  // Cache DOM et config en variables minimalistes
  let bar, prog, msg, pickIcon, homeIcon, 
      pickThreshold, homeThreshold, lastTotal = 0, 
      pending = false, needsUpdate = false,
      formatter;
  
  // Démarrage rapide
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  
  function init() {
    // Sélection ciblée des éléments essentiels
    bar = document.querySelector('[data-shipping-bar]');
    if (!bar) return;
    
    // Initialisation des éléments DOM et config
    prog = bar.querySelector('[data-progress-bar]');
    msg = bar.querySelector('[data-shipping-message]');
    pickIcon = bar.querySelector('[data-pickup-icon]');
    homeIcon = bar.querySelector('[data-home-icon]');
    
    if (!prog || !msg) return;
    
    // Lecture des seuils (une seule fois)
    pickThreshold = parseInt(bar.dataset.pickupThreshold) || 2950;
    homeThreshold = parseInt(bar.dataset.homeThreshold) || 4950;
    if (pickThreshold >= homeThreshold) pickThreshold = Math.floor(homeThreshold * 0.6);
    
    // Position des icônes (une seule fois)
    const pickPercent = Math.min(pickThreshold * 100 / homeThreshold, 100);
    if (pickIcon) pickIcon.style.left = `calc(${pickPercent}% - 15px)`;
    
    const pickLabel = bar.querySelector('.pickup-label');
    if (pickLabel) pickLabel.style.left = `calc(${pickPercent}% - 30px)`;
    
    // Initialiser le formateur de prix
    formatter = new Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'EUR'});
    
    // Liaison optimisée des événements essentiels
    bindEvents();
    
    // Première mise à jour
    updateCart();
  }
  
  function bindEvents() {
    // Observer pour détecter la visibilité
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        const visible = entries[0].isIntersecting;
        if (visible && needsUpdate) {
          needsUpdate = false;
          updateCart();
        }
      }).observe(bar);
    }
    
    // Événements unifiés pour les mises à jour du panier
    const events = ['cart:updated'];
    if (window.MinimogEvents && window.MinimogTheme) {
      events.push({type: 'subscribe', name: MinimogTheme.pubSubEvents.cartUpdate});
      events.push({type: 'subscribe', name: 'ON_ITEM_ADDED'});
    }
    
    // Ajout des écouteurs avec délégation
    events.forEach(evt => {
      if (typeof evt === 'string') {
        document.addEventListener(evt, () => queueUpdate());
      } else if (window.MinimogEvents) {
        MinimogEvents.subscribe(evt.name, () => queueUpdate());
      }
    });
    
    // Observer le drawer du panier (ouverture)
    const drawer = document.querySelector('m-cart-drawer');
    if (drawer) {
      new MutationObserver(muts => 
        muts.some(m => m.attributeName === 'open' && drawer.hasAttribute('open')) && updateCart()
      ).observe(drawer, {attributeFilter: ['open']});
    }
    
    // Écouteur d'ajout au panier unifié et passif
    document.addEventListener('click', e => {
      const t = e.target;
      (t.matches('[data-add-to-cart], .add-to-cart, [name="add"]') || 
       t.closest('[data-add-to-cart], .add-to-cart, [name="add"]')) && 
        setTimeout(queueUpdate, 200);
    }, {passive: true});
  }
  
  function queueUpdate() {
    if (pending) return;
    
    // Vérifier la visibilité pour économiser des ressources
    const visible = bar.getBoundingClientRect().top < window.innerHeight;
    if (!visible) {
      needsUpdate = true;
      return;
    }
    
    pending = true;
    setTimeout(() => {
      updateCart();
      pending = false;
    }, 100);
  }
  
  function updateCart() {
    fetch('/cart.js', {headers: {'Accept': 'application/json'}})
      .then(r => r.json())
      .then(cart => {
        // Extraire le total (sans frais de livraison)
        const total = cart.original_total_price ?? cart.total_price ?? 0;
        
        // Mettre à jour uniquement si nécessaire
        if (total !== lastTotal) {
          lastTotal = total;
          render(total);
        }
      })
      .catch(() => {});
  }
  
  function render(total) {
    // Calculer les valeurs en une seule fois
    const pickLeft = pickThreshold - total;
    const homeLeft = homeThreshold - total;
    const pickupPosition = pickThreshold * 100 / homeThreshold;
    
    // Regrouper les modifications DOM
    window.requestAnimationFrame(() => {
      // 1. Mettre à jour la barre de progression
      let width;
      if (homeLeft <= 0) {
        width = '100%';
        prog.className = 'progress-bar-done shimmer full-complete';
      } else if (pickLeft <= 0) {
        const beyondPickup = total - pickThreshold;
        const remainingToHome = homeThreshold - pickThreshold;
        const extraPercent = (beyondPickup * (100 - pickupPosition)) / remainingToHome;
        width = (pickupPosition + extraPercent) + '%';
        prog.className = 'progress-bar-done partial-complete';
      } else {
        width = Math.min((total * pickupPosition / pickThreshold), pickupPosition) + '%';
        prog.className = 'progress-bar-done';
      }
      prog.style.width = width;
      
      // 2. Mettre à jour les icônes
      if (pickIcon) pickIcon.classList.toggle('icon-complete', pickLeft <= 0);
      if (homeIcon) homeIcon.classList.toggle('icon-complete', homeLeft <= 0);
      
      // 3. Mettre à jour le message avec textes optimisés pour la conversion
      let message;
      if (pickLeft > 0) {
        // Premier palier pas encore atteint - créer un sentiment d'urgence
        message = `<span class="free_shipping_notice">🚚 <strong>Économisez sur la livraison !</strong> Plus que <strong>${formatMoney(pickLeft)}</strong> pour <strong>débloquer la livraison gratuite</strong> en point relais</span>`;
      } else if (homeLeft > 0) {
        // Premier palier atteint - encourager à atteindre le second
        message = `<span class="free_shipping_notice success-notice-partial">
          ✅ <strong>BRAVO !</strong> Livraison en point relais <strong>GRATUITE</strong> débloquée !<br>
          🏠 <strong>Offrez-vous</strong> la livraison à domicile ! Plus que <strong>${formatMoney(homeLeft)}</strong>
        </span>`;
      } else {
        // Tous les paliers atteints - confirmer le succès
        message = `<span class="free_shipping_notice success-notice">
          🎉 <strong>FÉLICITATIONS !</strong> Vous bénéficiez de la <strong>livraison GRATUITE</strong> pour toutes les options !<br>
          <span style="font-size:0.9em;">Votre commande est prête pour validation</span>
        </span>`;
      }
      
      if (msg.innerHTML !== message) msg.innerHTML = message;
    });
  }
  
  // Formater prix avec cache minimal
  const moneyCache = {};
  function formatMoney(cents) {
    const key = cents.toString();
    return moneyCache[key] || (moneyCache[key] = formatter.format(cents / 100));
  }
})();